'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';

import { refreshAssetCacheAndReload } from '@/lib/asset-cache-refresh';
import { reloadIfServerBuildDiffers } from '@/lib/stale-build';
import { useStreamingStore } from '@/lib/stores/streaming.store';
import { reportClientError } from '@/lib/telemetry/client-error';

/**
 * Chunk-load errors happen after a deploy: a still-open tab (or a request that
 * hits a not-yet-updated replica during the rollout) tries to import a hashed
 * chunk that no longer exists. Vite surfaces this as "Importing a module script
 * failed" / "Failed to fetch dynamically imported module"; older bundlers as
 * "Loading chunk …".
 *
 * They ALSO happen when the connection simply dropped for a moment, which looks
 * identical from here — so this only classifies the error shape. Whether a new
 * build is actually live is decided by asking the server (see below).
 */
function isChunkLoadError(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null;
  const name = err?.name ?? '';
  const msg = err?.message ?? '';
  return (
    name === 'ChunkLoadError' ||
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk/i.test(msg) ||
    /Loading CSS chunk/i.test(msg)
  );
}

/**
 * One automatic retry per cooldown window, tab-wide. Module scope on purpose:
 * this component remounts on every error, so component state cannot remember
 * that we already retried — and an unguarded retry loop against a chunk that
 * is genuinely gone would spin forever.
 */
const RETRY_COOLDOWN_MS = 30_000;
let lastAutoRetryAt = 0;

/**
 * Hard deadline on the "checking" state. ``fetchServerBuildId`` already carries
 * its own timeout, but a UI state that can only be left by a promise settling
 * is one hung request away from a dead end — and a dead end is strictly worse
 * than the error UI it replaced, because it has no button. Belt and braces:
 * whatever happens to the request, the user gets a way out.
 */
const CHECK_DEADLINE_MS = 5_000;

function claimAutoRetry(): boolean {
  const now = Date.now();
  if (now - lastAutoRetryAt < RETRY_COOLDOWN_MS) return false;
  lastAutoRetryAt = now;
  return true;
}

/** Test-only: drop the tab-wide retry cooldown between cases. */
export function _resetRetryCooldownForTests(): void {
  lastAutoRetryAt = 0;
}

type Phase = 'checking' | 'settled';

/**
 * Router-wide error boundary (`defaultErrorComponent`).
 *
 * On a chunk-load error it asks ``/version.json`` whether a newer build is
 * actually being served (KAN-196):
 *
 *   - **Different build** → reload to pick it up. This is the deploy case the
 *     self-healing was built for.
 *   - **Same build, or unreachable** → the failure was transient (dropped
 *     connection, laptop wake). Retry the route load once instead of throwing
 *     the page away.
 *   - **Same build and the retry failed too** → the chunk in this browser's
 *     cache is broken rather than missing on the server (KAN-512). Rewrite the
 *     cached copies from the network and reload once; only if that is declined
 *     does the manual-reload UI stay up.
 *
 * While a chat answer is streaming, no reload happens at all — the assistant
 * runtime lives above the router, so the stream keeps running and persists
 * server-side; a hard reload mid-answer would look like the answer was lost.
 * Any non-chunk error shows a friendly message, never the raw error text.
 *
 * The raw text still has to reach *us*, though: an error the user is left
 * looking at is reported home and stamped with a short reference they can quote
 * to support (KAN-398). Before that, a customer could sit in front of this page
 * for a morning without a single trace of the crash on our side.
 */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const t = useTranslations('errors.client');
  const router = useRouter();
  const isChunkError = isChunkLoadError(error);
  const hasActiveStreams = useStreamingStore((s) => s.activeStreams.size > 0);
  const deferReload = isChunkError && hasActiveStreams;
  // Non-chunk errors have nothing to check — show their UI immediately.
  const [phase, setPhase] = useState<Phase>(() =>
    isChunkError && !hasActiveStreams ? 'checking' : 'settled'
  );

  useEffect(() => {
    if (!isChunkError || hasActiveStreams) {
      setPhase('settled');
      return;
    }
    let cancelled = false;
    const deadline = setTimeout(() => {
      if (!cancelled) setPhase('settled');
    }, CHECK_DEADLINE_MS);

    const message = error instanceof Error ? error.message : String(error);
    void reloadIfServerBuildDiffers('route-chunk-error', { detail: message }).then((reloading) => {
      clearTimeout(deadline);
      if (cancelled || reloading) return;
      // Same build: a transient failure. Re-run the route load rather than
      // reloading the document — nothing about the page is actually stale.
      if (claimAutoRetry()) {
        void router.invalidate();
        reset();
        return;
      }
      // The retry is spent and the chunk still fails while the server is
      // serving this very build: the copy in *this browser's* cache is broken
      // (KAN-512). A plain reload would hand the same bytes back — assets are
      // cached `immutable` for a year — so rewrite the cached copies from the
      // network first, then reload.
      void refreshAssetCacheAndReload(message).then((reloading) => {
        if (!cancelled && !reloading) setPhase('settled');
      });
    });
    return () => {
      cancelled = true;
      clearTimeout(deadline);
    };
  }, [isChunkError, hasActiveStreams, error, router, reset]);

  // Report only once the user is actually left with the error page — not while
  // the self-healing is still running, and not on the "new version" page, which
  // announces an update rather than a fault. A chunk error that reloads or
  // retries away is already covered by the reload report; logging it here too
  // would put a WARNING in the log for every stale tab after every deploy.
  const [reference, setReference] = useState<string | null>(null);
  const settledOnError = phase === 'settled' && !deferReload;
  useEffect(() => {
    if (!settledOnError) return;
    setReference(reportClientError({ source: 'route-error-boundary', error }));
  }, [settledOnError, error]);

  if (phase === 'checking') {
    return (
      <div className="flex min-h-svh items-center justify-center px-4">
        <p className="text-sm text-muted-foreground">{t('checking')}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h2 className="mb-4 text-xl font-semibold text-foreground">
          {deferReload ? t('updateTitle') : t('title')}
        </h2>
        {deferReload && (
          <p className="mb-6 text-sm text-muted-foreground">{t('updateStreaming')}</p>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
        >
          {deferReload ? t('reloadNow') : t('reload')}
        </button>
        {/* Only ever the reference of a report that actually went out. A code
            support cannot find is worse than no code at all. */}
        {reference && (
          <p className="mt-6 text-xs text-muted-foreground">{t('reference', { reference })}</p>
        )}
      </div>
    </div>
  );
}
